import React from "react";
import Button from "react-bootstrap/Button";
import _ from "lodash";
import { useNavigate } from "react-router-dom";
import BaseHeader from "@/components/Common/BaseHeader";
import Introductions from "@/components/HeaderButtons/Introductions";
import Notifications from "@/components/HeaderButtons/Notifications";
import DeleteNotificationModalNew from "@/components/Onboarding/DeleteNotificationModalNew";
import WantAdRenewConfirmationModal from "@/components/Onboarding/WantAdRenewConfirmationModal";
import { SIGNUP, LOGIN, POST_WANT_AD } from "@/constants/onBoarding";
import { pageURLs } from "@/constants";
import Client from "@/images/v2/Client-icon.svg";
import Home from "@/images/v2/Home-icon.svg";
import Inventory from "@/images/v2/Inventory.svg";
import Wantlisting from "@/images/v2/Wantlisting.svg";
import BellIcon from "@/images/v2/Bell-icon.svg";
import HandshakeIcon from "@/images/v2/Handshake-icon.svg";
import { BaseSidebarWrapper, HeaderSidebarBaseWrapper, HeaderSidebarDropdownMenu, SidebarItem } from "./HeaderSidebar";
import MobileBottomNav from "./MobileBottomNav";
import { useUserDetails } from "@/context";
import { useMLSAgentAtGitchaRedirect } from "@/hooks/useMLSAgentAtGitchaRedirect";
import { useAppConfig } from "@/appConfig";


const Sidebar = (props) => {

  const {
    shouldToggleColor,
    currentURL,
    handleGoFeed,
    handleGoInventory,
    handleGoClients,
    handleGoWantListings,
    handleGoMlsListings,
    showMlsListings,
  } = props

  return (
    <BaseSidebarWrapper shouldToggleColor={shouldToggleColor}>
      <SidebarItem
        label="Feed"
        icon={Home}
        onClick={handleGoFeed}
        isActive={currentURL === "/" || currentURL === pageURLs.home}
      />

      <SidebarItem
        label={
          <>
            My <br /> Want-Listings
          </>
        }
        icon={Wantlisting}
        isActive={currentURL === pageURLs.agentMyWantAdListings}
        onClick={handleGoWantListings}
      />

      <SidebarItem
        label={"My Clients"}
        icon={Client}
        onClick={handleGoClients}
        isActive={currentURL === pageURLs.agentMyClients}
      />

      <SidebarItem
        icon={Inventory}
        label={"My Inventory"}
        isActive={currentURL === pageURLs.agentInventoryListing}
        onClick={handleGoInventory}
      />

      {showMlsListings && (
        <SidebarItem
          icon={Inventory}
          label={
            <>
              MLS <br /> Listings
            </>
          }
          isActive={
            typeof currentURL === "string" &&
            currentURL.startsWith("/mls-listing")
          }
          onClick={handleGoMlsListings}
        />
      )}
    </BaseSidebarWrapper>
  );
};

const HeaderSidebarAgent = React.forwardRef((allProps, headerRef) => {

  const {
    user,
    userDetails,
    userPlan,
    showAgentMenuNotificationIcon,
    currentURL,
    handleAgentOnboardingModal,
    PremiumPlanModalShow,
    handleLogOut,
    headerHeight,
    handleGoFeed,
    handleGoInventory,
    goTo,
    ContactModalShow,
    BasicPlanModalShow,
    shouldDisplayWantListingButton,
    introductionList,
    isLoadIntroList,
    unreadMessageCount,
    notificationList,
    isLoadNotificationList,
    unreadNotificationMessageCount,
    unreadInappNotificationCount,
    deleteNotificationModal,
    wantAdRenewModal,
    handleHandShakeClick,
    updateIntroStateonHover,
    updateIntroPageOnScroll,
    handleCloseIntroList,
    updateInAppStatusonHover,
    updateInAppDeleteStatusOnClick,
    renewWantAd,
    onDeleteInappNotification,
    onConfirmReniewWantAdClicked,
    handleDeleteNotificationPopupClose,
    handleWantAdRenewPopupClose,
    isGitchaDefaultWebsite,
  } = allProps;

  const navigate = useNavigate();

  const handlePostWantAdClick = () => {
    const hasUser = _.get(user, "userInfo", null);

    if (!hasUser) {
      return handleAgentOnboardingModal({
        showPopup: true,
        userAction: SIGNUP,
        userPreference: POST_WANT_AD,
      });
    }

    return navigate(pageURLs.agentCreatewantadMainScreen);
  };

  const handleGoClients = () => {
    const userInfo = _.get(user, "userInfo", null);
    if (!userInfo) {
      handleAgentOnboardingModal({ showPopup: true, userAction: SIGNUP });
      return;
    }
    if (window.location.pathname !== pageURLs.agentMyClients) {
      goTo(pageURLs.agentMyClients);
    }
  };

  const handleGoWantListings = () => {
    const userInfo = _.get(user, "userInfo", null);
    if (!userInfo) {
      handleAgentOnboardingModal({ showPopup: true, userAction: SIGNUP });
      return;
    }
    if (window.location.pathname !== pageURLs.agentMyWantAdListings) {
      goTo(pageURLs.agentMyWantAdListings);
    }
  };

  const handleGoMlsListings = () => {
    const userInfo = _.get(user, "userInfo", null);
    if (!userInfo) {
      handleAgentOnboardingModal({ showPopup: true, userAction: SIGNUP });
      return;
    }
    goTo(`${pageURLs.agentInventoryListing}?tab=mls`);
  };

  const { shouldToggleColor } = useUserDetails();
  const { flags } = useAppConfig();
  const showMlsListings = !!flags?.mlsListings;

  const { redirectToBLSEnv }= useMLSAgentAtGitchaRedirect();

  const headerContent = HeaderSidebarBaseWrapper({
    ...allProps,
    headerExtraButtons: () => {

      if (!shouldDisplayWantListingButton) {
        return null;
      }

      return (
        <Button
          variant={shouldToggleColor ? "tertiary" : "primary"}
          className="ml-6 mr-2 btn-tweak"
          onClick={handlePostWantAdClick}
        >
          Post Want-Listing
        </Button>
      )
    },
    sidebar: <Sidebar
      currentURL={currentURL}
      handleGoInventory={handleGoInventory}
      handleGoFeed={handleGoFeed}
      handleGoClients={handleGoClients}
      handleGoWantListings={handleGoWantListings}
      handleGoMlsListings={handleGoMlsListings}
      showMlsListings={showMlsListings}
      shouldToggleColor={shouldToggleColor}
    />,
    mobileNav: <MobileBottomNav
      leftItems={[
        { icon: Home, label: "Feed", onClick: handleGoFeed, isActive: currentURL === "/" || currentURL === pageURLs.home },
        { icon: Client, label: "My Clients", onClick: () => redirectToBLSEnv({ path: pageURLs.agentMyClients, onGitchaEnvFallback: handleGoClients }), isActive: currentURL === pageURLs.agentMyClients },
      ]}
      addMenuItems={[
        { icon: Wantlisting, label: "Want-Listing", onClick: handleGoWantListings, isActive: currentURL === pageURLs.agentMyWantAdListings },
        { icon: Inventory, label: "Inventory", onClick: () => redirectToBLSEnv({ path: pageURLs.agentInventoryListing, onGitchaEnvFallback: handleGoInventory }), isActive: currentURL === pageURLs.agentInventoryListing },
      ]}
      onAddClick={!_.get(user, "userInfo", null) ? () => handleAgentOnboardingModal({ showPopup: true, userAction: isGitchaDefaultWebsite ? SIGNUP : LOGIN }) : null}
      notificationConfig={_.get(user, "userInfo", null) ? {
        icon: BellIcon,
        totalBadge: (unreadMessageCount || 0) + (unreadNotificationMessageCount || 0) + (unreadInappNotificationCount || 0),
        items: [
          {
            icon: HandshakeIcon,
            label: "Introductions",
            panelKey: "introductions",
            badge: unreadMessageCount || 0,
            onOpen: handleHandShakeClick,
          },
          {
            icon: BellIcon,
            label: "Notifications",
            panelKey: "notifications",
            badge: unreadNotificationMessageCount || unreadInappNotificationCount || 0,
            onOpen: updateInAppStatusonHover,
          },
        ],
        panels: {
          introductions: (
            <Introductions
              list={introductionList}
              updateIntroStateonHover={updateIntroStateonHover}
              updateIntroPageOnScroll={updateIntroPageOnScroll}
              isLoadIntroList={isLoadIntroList}
              handleCloseIntroList={handleCloseIntroList}
            />
          ),
          notifications: (
            <div className="w-100 h-100 position-relative notif-modal-container">
              <Notifications
                list={notificationList}
                isLoadNotificationList={isLoadNotificationList}
                onDeleteInappNotification={() => onDeleteInappNotification()}
                updateInAppDeleteStatusOnClick={updateInAppDeleteStatusOnClick}
                renewWantAd={(notification) => renewWantAd(notification)}
              />
              <DeleteNotificationModalNew
                show={deleteNotificationModal}
                onHide={handleDeleteNotificationPopupClose}
                onConfirm={updateInAppDeleteStatusOnClick}
              />
              <WantAdRenewConfirmationModal
                show={wantAdRenewModal}
                onHide={handleWantAdRenewPopupClose}
                onConfirmReniewWantAd={onConfirmReniewWantAdClicked}
              />
            </div>
          ),
        },
      } : null}
    />,
    authButtonsProps: {
      signInText: "Login",
      handleAgentOnboardingModal: handleAgentOnboardingModal,
    },
    headerSidebarDropdownMenuProps: {
      user,
      userDetails,
      userPlan,
      showAgentMenuNotificationIcon,
      navigate,
      PremiumPlanModalShow,
      handleLogOut,
      ContactModalShow,
      BasicPlanModalShow
    },
  })

  return <BaseHeader {...allProps} headerContent={headerContent} />;

})

export default HeaderSidebarAgent;
